import type { Maybe } from '@adornis/base/utilTypes.js';
import type { UserWithMemberships } from './db/SaraaUser.js';
import { permissionFor } from './permissionName.js';

/**
 * What each kind of user may ever do.
 *
 * This table used to live in `role-assignments` and `roles` documents, seeded from
 * code but editable afterwards through the ERM permission matrix — so the code
 * read as authoritative while the database quietly outranked it. It is now the
 * only definition: in git, reviewed, and asserted against the scope policies by
 * `assertCheckNamesExist` at boot and in the tests.
 *
 * A grant's `checks` decide *where* the permission applies. An empty array means
 * globally; otherwise each name must be registered for that permission by the
 * scope that owns it, and the first one to pass wins.
 */
export interface Grant {
  permission: string;
  checks: string[];
}

/**
 * Uses the same `permissionFor` the scopes do — that identity is what makes a
 * grant here line up with the permission a scope registers, and
 * `assertCheckNamesExist` is what proves it still holds.
 */
const g = (scope: string, action: string, checks: string[] = []): Grant => ({
  permission: permissionFor(scope, action),
  checks,
});

const ORG_ADMIN_SCOPED = ['isOrgAdminOf', 'isOrgAdminPageCheck'];
const ORG_MEMBER_SCOPED = ['isOrgMemberOf', 'isOrgMemberPageCheck'];
const TARGET_USER_SCOPED = ['isOrgAdminOfTargetUser', 'isOrgAdminPageCheck'];

const CRUD = ['view', 'create', 'modify', 'delete'] as const;

/**
 * A platform administrator: everything, everywhere, unconditionally.
 *
 * Genuinely global now (`checks: []`). Under the old model these had to be
 * scoped to an `isAdmin` check instead, because grants from several roles merged
 * by concatenating their check arrays and a global grant unioned with a
 * check-scoped one collapsed to check-scoped — silently stripping an admin who
 * also held an org role. A user resolves to exactly one of these two sets now, so
 * there is no union and no sentinel permission left to work around it.
 */
export const ADMIN_GRANTS: readonly Grant[] = [
  ...CRUD.map(action => g('templates', action)),
  ...CRUD.map(action => g('templateFields', action)),
  ...CRUD.map(action => g('organizations', action)),
  ...CRUD.map(action => g('users', action)),
  g('users', 'manageMemberships'),
  g('users', 'changePassword'),
  g('generate', 'use'),
];

/**
 * Everyone else.
 *
 * The union of what used to be the ORG_ADMIN and ORG_USER roles — which every
 * non-admin held together anyway, as a fixed "capability envelope". Merging them
 * changes nothing and removes the last reason to model roles at all: the checks
 * resolve against `memberships`, so an ORG_USER gains nothing from the
 * admin-scoped grants below. Where a permission appeared in both roles, the org
 * admin's checks are the superset and the user's `isSelf` is kept alongside.
 */
export const ORG_GRANTS: readonly Grant[] = [
  g('templates', 'view', ORG_MEMBER_SCOPED),
  g('templates', 'create', ORG_ADMIN_SCOPED),
  g('templates', 'modify', ORG_ADMIN_SCOPED),
  g('templates', 'delete', ORG_ADMIN_SCOPED),

  g('templateFields', 'view', ORG_MEMBER_SCOPED),
  g('templateFields', 'create', ORG_ADMIN_SCOPED),
  g('templateFields', 'modify', ORG_ADMIN_SCOPED),
  g('templateFields', 'delete', ORG_ADMIN_SCOPED),

  // No create/delete: bringing an organization into existence stays with a
  // platform admin.
  g('organizations', 'view', ORG_MEMBER_SCOPED),
  g('organizations', 'modify', ORG_ADMIN_SCOPED),

  g('users', 'view', [...TARGET_USER_SCOPED, 'isSelf']),
  g('users', 'create', TARGET_USER_SCOPED),
  g('users', 'modify', [...TARGET_USER_SCOPED, 'isSelf']),
  g('users', 'delete', TARGET_USER_SCOPED),

  /**
   * Deliberately without `isSelf`, unlike `modify`. Editing your own profile is
   * ordinary; granting yourself a membership is escalation. Sharing checks between
   * the two would let anyone hand themselves ORG_ADMIN of any organization.
   */
  g('users', 'manageMemberships', TARGET_USER_SCOPED),

  /**
   * Scoped to the target rather than to a page: `isOrgAdminOfTargetUser` already
   * allows a user acting on themselves, and `isSelf` says so out loud. There is
   * deliberately no page check in the list — see the action in `scopes.ts`.
   */
  g('users', 'changePassword', ['isOrgAdminOfTargetUser', 'isSelf']),

  g('generate', 'use', ORG_MEMBER_SCOPED),
];

/**
 * Grant availability as a pure function of the user.
 *
 * Note what this is *not*: the final decision. It answers "which permissions could
 * this user hold, and under what conditions" — the conditions are the checks, and
 * they still need the requested action and the target payload to resolve. Only the
 * availability is static.
 */
export const resolveGrantsFor = (user: Maybe<UserWithMemberships>): Map<string, string[]> => {
  const grants = user?.isPlatformAdmin === true ? ADMIN_GRANTS : ORG_GRANTS;
  return new Map(grants.map(grant => [grant.permission, grant.checks]));
};
